import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { Crosshair, RotateCcw } from 'lucide-react';
import { ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import LandmarkPanel from '~/components/Landmarks/LandmarkPanel';
import RunTreSection from './RunTreSection';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import { Note, Progress } from './QaStageNotes';
import RegistrationQaMeasurement from './RegistrationQaMeasurement';
import { useQaContourComparison } from './useQaContourComparison';
import { useRegistrationQaRun } from './useRegistrationQaRun';
import { useRunLandmarkComparison } from './useRunLandmarkComparison';

function Section({
    titleId,
    children,
}: {
    titleId: string;
    children: ReactNode;
}) {
    return (
        <Stack spacing={0.75}>
            <Typography
                fontSize={11}
                fontWeight={600}
                color="text.primary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
            >
                <FormattedMessage id={titleId} />
            </Typography>
            {children}
        </Stack>
    );
}

export default function RegistrationQaStage() {
    const intl = useIntl();
    const selectedTool = useRecoilValue(selectedToolState);
    const setTool = useSetRecoilState(selectedToolState);
    const {
        phase,
        blockedReason,
        attempted,
        calculate,
        planningContourSet,
        targetContourSet,
    } = useRegistrationQaRun();
    // Both rows show the Contour+ sets the measurement compares: the target's in the primary row, the source's in the reference row.
    useRunLandmarkComparison(true, planningContourSet);
    useQaContourComparison(targetContourSet);

    // Measured as soon as the step opens, once per run.
    useEffect(() => {
        if (!blockedReason && !attempted) {
            calculate();
        }
    }, [blockedReason, attempted, calculate]);

    if (selectedTool === 'landmark') {
        return <LandmarkPanel />;
    }

    return (
        <Stack px={1.5} pt={1} pb={1.5} spacing={1.5}>
            <Section titleId="replanning.qa.contourSection">
                <Typography fontSize={11} color="text.secondary">
                    <FormattedMessage id="replanning.qa.explain" />
                </Typography>

                {blockedReason && (
                    <Typography
                        fontSize={11}
                        color="text.secondary"
                        data-testid="replanning-qa-blocked"
                    >
                        {intl.formatMessage({ id: blockedReason })}
                    </Typography>
                )}

                {phase === 'segmenting' && (
                    <Progress
                        id="replanning.qa.segmenting"
                        testId="replanning-qa-segmenting"
                    />
                )}

                {(phase === 'failed' || phase === 'cancelled') && (
                    <Stack spacing={0.75} alignItems="flex-start">
                        <Note
                            id={
                                phase === 'failed'
                                    ? 'replanning.qa.failed'
                                    : 'replanning.qa.canceled'
                            }
                            testId="replanning-qa-failed"
                            tone="warning.main"
                        />
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RotateCcw size={12} />}
                            onClick={calculate}
                            data-testid="replanning-qa-retry"
                            sx={{ fontSize: 11, textTransform: 'none' }}
                        >
                            <FormattedMessage id="replanning.qa.retry" />
                        </Button>
                    </Stack>
                )}

                {phase === 'ready' && <RegistrationQaMeasurement />}
            </Section>

            <Divider />

            <Section titleId="replanning.qa.landmarkSection">
                <Typography fontSize={11} color="text.secondary">
                    <FormattedMessage id="replanning.qa.landmarkExplain" />
                </Typography>
                <RunTreSection />
                <Box>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Crosshair size={12} />}
                        onClick={() => setTool('landmark')}
                        data-testid="registration-show-landmark-panel"
                        sx={{ fontSize: 11, textTransform: 'none' }}
                    >
                        <FormattedMessage id="landmark.showPanel" />
                    </Button>
                </Box>
            </Section>
        </Stack>
    );
}
